import React from 'react';
import { styled } from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CalendarTodayOutlined } from '@mui/icons-material';
import moment from 'moment';
import { Colors } from 'library';

interface DatePickerProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    monthYearOnly?: boolean;
    fullWidth?: boolean;
    minDate?: Date;
    maxDate?: Date;
}

export function DatePicker({
    value,
    onChange,
    placeholder = 'Pick a date',
    monthYearOnly = false,
    fullWidth = false,
    minDate,
    maxDate,
}: DatePickerProps) {
    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <StyledDatePicker
                fullWidth={fullWidth}
                value={value ? moment(value) : null}
                onChange={(newValue: moment.Moment | Date | null) =>
                    onChange(newValue ? moment(newValue).format(monthYearOnly ? 'YYYY-MM-01' : 'YYYY-MM-DD') : '')
                }
                format={monthYearOnly ? 'MM/YYYY' : 'MM/DD/YYYY'}
                views={monthYearOnly ? ['year', 'month'] : undefined}
                minDate={minDate ? moment(minDate) : undefined}
                maxDate={maxDate ? moment(maxDate) : undefined}
                slots={{
                    openPickerIcon: StyledCalendarIcon,
                }}
                slotProps={{
                    textField: {
                        placeholder,
                        fullWidth,
                    },
                    inputAdornment: {
                        position: 'start',
                    },
                    openPickerButton: {
                        disableRipple: true,
                        sx: {
                            color: Colors.textSecondary,
                            '&:focus, &:focus-visible': { outline: 'none' },
                        },
                    },
                    desktopPaper: {
                        sx: {
                            '& .MuiPickersMonth-monthButton, & .MuiPickersYear-yearButton, & .MuiPickersDay-root': {
                                '&:focus, &:focus-visible': { outline: 'none' },
                            },
                        },
                    },
                }}
            />
        </LocalizationProvider>
    );
}

function StyledCalendarIcon() {
    return <CalendarTodayOutlined sx={{ fontSize: 20 }} />;
}

const StyledDatePicker = styled(MuiDatePicker, {
    shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
    maxWidth: fullWidth ? '100%' : 280,
    width: fullWidth ? '100%' : undefined,
    '& .MuiOutlinedInput-root': {
        borderRadius: fullWidth ? 10 : 24,
        backgroundColor: Colors.surface,
        fontSize: 14,
        fontFamily: 'inherit',
        color: Colors.primary,
        '& fieldset': {
            borderColor: Colors.border,
        },
        '&:hover fieldset': {
            borderColor: Colors.textSecondary,
        },
        '&.Mui-focused fieldset': {
            borderColor: Colors.textSecondary,
            borderWidth: 1,
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '10px 14px 10px 0',
    },
    '& .MuiInputLabel-root': {
        display: 'none',
    },
}));
